import { InjectionToken } from '@angular/core';
import { OktaAuth } from '@okta/okta-auth-js';
import { Configuration, getConfigKeyForProject } from '@content-platform/web-angular-shared-util-configuration';
import { getSecretsEndpointFullUrl } from './secrets-endpoint.util';
import { ensureMergedStructure } from './config-structure-handler.util';
import { getOktaConfigForApp } from './okta-config-provider.util';
import { getEnvironmentFromLocation } from './environment-detector.util';
import { mergeWithLocalConfig, isDevelopmentMode } from './local-config-override.util';
import { isEncryptedPayload, decryptPayload } from './aes-decryption.util';
/**
 * Injection token for pre-bootstrap user info.
 * This allows the user info fetched during pre-bootstrap to be injected into Angular services.
 */
export const PRE_BOOTSTRAP_USER_INFO = new InjectionToken('pre-bootstrap-user-info');
/**
 * Suppresses console output in production environments (UAT & Prod).
 * Overrides console.log/debug/info/warn with no-ops so no sensitive information
 * (API URLs, token states, config keys) leaks to the browser Developer Tools.
 * Applies to all code — our own, Angular internals, and third-party scripts.
 *
 * Called once from authenticateAndLoadConfigsBeforeBootstrap() which every app
 * invokes from main.ts, so all frontend apps are covered with zero per-app changes.
 *
 * console.error is intentionally preserved for critical runtime diagnostics.
 */
function suppressConsoleInProduction() {
    const env = getEnvironmentFromLocation();
    if (env === 'dev' || env === 'uat' || env === 'qa') {
        return;
    }
    const noop = () => { };
    /* eslint-disable no-console */
    console.log = noop;
    console.debug = noop;
    console.info = noop;
    console.warn = noop;
    /* eslint-enable no-console */
}
/**
 * Recursively collects all keys from a nested object using dot-notation paths.
 * e.g. { shared: { accurate: { licenseKey: 'x' } } } → ["shared", "shared.accurate", "shared.accurate.licenseKey"]
 */
function getAllKeys(obj, prefix = '') {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
        return [];
    }
    const keys = [];
    for (const key of Object.keys(obj)) {
        const fullKey = prefix ? `${prefix}.${key}` : key;
        keys.push(fullKey);
        keys.push(...getAllKeys(obj[key], fullKey));
    }
    return keys;
}
// Track if New Relic loader has been injected
let isNewRelicLoaderInjected = false;
/**
 * Loads New Relic agent scripts.
 * This is a standalone version that can run before Angular bootstrap.
 */
function loadNewRelicScripts() {
    if (isNewRelicLoaderInjected) {
        console.log('ℹ️ New Relic scripts already loaded, skipping...');
        return;
    }
    // NR loader script
    const nrLoader = document.createElement('script');
    nrLoader.src = 'https://js-agent.newrelic.com/nr-loader-spa-1.258.1.min.js';
    nrLoader.type = 'text/javascript';
    nrLoader.async = true;
    nrLoader.onload = () => {
        console.log('✅ New Relic loader loaded');
    };
    document.head.appendChild(nrLoader);
    isNewRelicLoaderInjected = true;
}
/**
 * Initializes New Relic with configuration from secrets.
 * Called after configs are loaded but before Angular bootstrap.
 * @param newRelicConfig - The 'new-relic' config from secrets
 */
function initializeNewRelic(newRelicConfig) {
    if (!newRelicConfig) {
        console.log('ℹ️ No New Relic configuration found in secrets, skipping...');
        return;
    }
    // Update New Relic config in window.NREUM via Configuration service
    const updated = Configuration.updateNewRelicConfig(newRelicConfig);
    if (updated) {
        // Load New Relic scripts
        loadNewRelicScripts();
        console.log('✅ New Relic initialized before bootstrap');
    }
}
/**
 * Initializes Okta SDK and authenticates user before Angular bootstrap.
 * Returns OktaAuth instance and authentication status.
 */
export async function initializeOktaBeforeBootstrap(oktaConfig) {
    // Initialize Okta SDK
    const oktaAuth = new OktaAuth(oktaConfig);
    // Handle redirect if returning from Okta login
    if (oktaAuth.isLoginRedirect()) {
        try {
            await oktaAuth.handleLoginRedirect();
            // After handling redirect, user should be authenticated
        }
        catch (error) {
            console.error('Error handling Okta login redirect:', error);
            throw error;
        }
    }
    // Check authentication status
    const isAuthenticated = await oktaAuth.isAuthenticated();
    // If not authenticated, redirect to login
    if (!isAuthenticated) {
        console.log('🚀🚀 -- User not authenticated, redirecting to Okta login --');
        await oktaAuth.signInWithRedirect({ originalUri: window.location.href });
        // This will redirect away, so code after this won't execute
        return { oktaAuth, isAuthenticated: false };
    }
    return { oktaAuth, isAuthenticated: true };
}
/**
 * Loads all configurations from Secrets Manager before Angular bootstrap.
 * Requires Okta token to be available.
 *
 * In development mode (localhost), also fetches and merges local config.json
 * from /{projectName}/config/config.json for local overrides.
 *
 * @param projectName - The project folder name (e.g., 'c3-linear-monitoring')
 *                      Used for local config path: /c3-linear-monitoring/config/config.json
 *                      Also used to determine the config key for Secrets Manager lookup
 * @param oktaToken - The Okta access token for authentication
 * @param environment - The current environment (dev, qa, uat, prod)
 */
export async function loadConfigsBeforeBootstrap(projectName, oktaToken, environment) {
    // Get the Secrets Manager config key from project name
    // e.g., 'c3-linear-monitoring' → 'c3'
    const configKey = getConfigKeyForProject(projectName);
    // Get hardcoded secrets endpoint URL
    const secretsEndpointUrl = getSecretsEndpointFullUrl();
    console.log('🚀🚀 -- Fetching ALL configs from secrets endpoint:', secretsEndpointUrl);
    console.log('🚀🚀 -- Project name:', projectName);
    console.log('🚀🚀 -- Config key (for Secrets Manager):', configKey);
    console.log('🚀🚀 -- Environment:', environment);
    // Fetch configs from Secrets Manager using the mapped config key
    const response = await fetch(`${secretsEndpointUrl}?app=${configKey}`, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${oktaToken}`
        }
    });
    let baseConfig;
    if (!response.ok) {
        if (response.status === 401) {
            throw new Error('Unauthorized: Invalid or expired Okta token');
        }
        // In development (e.g. new app with no secret yet), use default config so app can load
        if (response.status === 404 && isDevelopmentMode()) {
            console.warn(`⚠️ No config found in Secrets Manager for app "${configKey}" (404). ` +
                'Using default config so the app can load locally. Add app-specific secret when ready.');
            baseConfig = ensureMergedStructure({ launchDarkly: {}, environment: {}, api: {} }, configKey);
        }
        else {
            throw new Error(`Failed to load configs: ${response.status} ${response.statusText}`);
        }
    }
    else {
        const responseBody = await response.json();
        // feconfigs responses are AES-256-GCM encrypted in every env. A plaintext
        // body means either the backend regressed or somebody is MITM'ing the
        // response — refuse to bootstrap rather than silently accept it.
        if (!isEncryptedPayload(responseBody)) {
            throw new Error(`Unexpected plaintext config response from feconfigs for app "${configKey}". ` +
                `Backend must return an AES-256-GCM encrypted payload ({ iv, tag, data }).`);
        }
        console.log(`🔒 [${configKey}] Encrypted config response detected (iv: ${responseBody.iv.substring(0, 8)}...) — decrypting...`);
        const secretsConfig = await decryptPayload(responseBody);
        console.log(`✅ [${configKey}] Config decrypted successfully — keys:`, getAllKeys(secretsConfig));
        // Backend returns already-merged config (globals + app-specific)
        // ensureMergedStructure validates the structure and logs for debugging
        baseConfig = ensureMergedStructure(secretsConfig, configKey);
    }
    // In development mode, merge with local config.json if it exists
    // Uses projectName (folder name) for local config path: /c3-linear-monitoring/config/config.json
    const mergedConfig = await mergeWithLocalConfig(baseConfig, projectName);
    // Set active app name using the mapped config key
    Configuration.setActiveAppName(configKey);
    // Set configs in Configuration service (before Angular bootstrap)
    Configuration.setConfig(mergedConfig);
    console.log('✅ Configs loaded and set in Configuration service');
    console.log('🚀🚀 -- Config keys:', Object.keys(mergedConfig));
    // Initialize New Relic with config from secrets
    // The key in secrets is 'new-relic' (with hyphen)
    initializeNewRelic(mergedConfig['new-relic']);
    return mergedConfig;
}
/**
 * Main function: Authenticates user and loads configs before bootstrap.
 * This is called from main.ts before platformBrowserDynamic().
 *
 * @param projectName - The project folder name as it appears in apps/ directory
 *                      (e.g., 'c3-linear-monitoring', 'library-manager', 'fulfillment')
 *                      This should match the "name" field in project.json
 * @param baseHref - Optional base href for the app (used for Okta redirect URI)
 * @returns Promise with OktaAuth instance, loaded configs, and user info
 *
 * NAMING CONVENTION:
 * - projectName: The actual folder name (e.g., 'c3-linear-monitoring')
 *   → Used for local config path: /c3-linear-monitoring/config/config.json (dev only)
 * - configKey: Derived from projects-map.ts (e.g., 'c3')
 *   → Used for Secrets Manager: vcap-configs-c3
 *   → Used for Configuration.setActiveAppName()
 */
export async function authenticateAndLoadConfigsBeforeBootstrap(projectName, baseHref) {
    // Suppress console output in production before any other code runs
    suppressConsoleInProduction();
    // Determine environment
    const environment = getEnvironmentFromLocation();
    // Get Okta configuration using the mapped config key
    const oktaConfig = getOktaConfigForApp(environment, baseHref);
    // Step 1: Initialize Okta and authenticate
    const { oktaAuth, isAuthenticated } = await initializeOktaBeforeBootstrap(oktaConfig);
    if (!isAuthenticated) {
        // User will be redirected to Okta login
        // After login, they'll return and this function will be called again
        throw new Error('User not authenticated - redirecting to login');
    }
    // Step 2: Get Okta token
    const oktaToken = oktaAuth.getAccessToken();
    if (!oktaToken) {
        throw new Error('No Okta token available after authentication');
    }
    // Step 3: IMPORTANT - Fetch user info from Okta BEFORE loading configs
    // This ensures userId is available for subsequent GraphQL calls
    // The userinfo endpoint populates oktaAuth.authStateManager with user profile
    let fetchedUserInfo = null;
    try {
        // oktaAuth.getUser() returns UserClaims which is compatible with OktaUserInfo
        fetchedUserInfo = await oktaAuth.getUser();
        console.log('✅ User info loaded from Okta:', fetchedUserInfo?.email || fetchedUserInfo?.sub);
    }
    catch (error) {
        console.warn('⚠️ Failed to fetch user info from Okta:', error);
    }
    // Step 4: Load configs from Secrets Manager
    // projectName is passed so local config uses folder path, secrets use mapped key
    const configs = await loadConfigsBeforeBootstrap(projectName, oktaToken, environment);
    // These values are passed through Angular's DI system
    // - oktaAuth is provided via OKTA_AUTH token in platformBrowserDynamic()
    // - userInfo is provided via PRE_BOOTSTRAP_USER_INFO token in platformBrowserDynamic()
    return { oktaAuth, configs, userInfo: fetchedUserInfo };
}
//# sourceMappingURL=pre-bootstrap-config-loader.util.js.map